import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormGroup,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Collaborator, getCollaboratorByName } from "src/apis/company";
import {
  createService,
  CreateServicePayload,
  getServiceById,
  ServiceStatus,
  updateService,
} from "src/apis/services";
import CollaboratorSelect from "src/pages/components/CollaboratorSelect";
import CompanySelect from "src/pages/components/CompanySelect";
import FormSaveAndResetButtons from "src/pages/components/FormSaveAndResetButtons";
import OptionalFieldHelperText from "src/pages/components/OptionalFieldHelperText";
import PageContainer from "src/pages/components/PageContainer";
import ProntuarioDrawer from "src/pages/components/prontuario/ProntuarioDrawer";
import {
  getUserId,
  isUserAdministrator,
  isUserPsychologist,
} from "src/utils/auth";
import {
  getFirstDayOfMonth,
  getToday,
  removeHoursFromDate,
} from "src/utils/date";
import { isSuccessOrAllRequiredFieldsAreFilled } from "src/utils/form";
import { extractErrorMessage } from "src/utils/http";
import { Id } from "src/utils/types";
import { BlockStatus, getBlockStatus, getSoftBlockStatus } from "src/apis/upsell";

const initialValues: CreateServicePayload = {
  collaborator_id: "",
  company_id: "",
  area: "",
  department: "",
  city: "",
  date_of_service: "",
  annotations: "",
  complaint: "",
  service_status: "Realizado",
  edit_reason: "",
};

const optionalFields: string[] = [
  "area",
  "department",
  "city",
  "annotations",
  "complaint",
  "edit_reason",
];

const RegisterService: FC = (): JSX.Element => {
  const [formValues, setFormValues] =
    useState<CreateServicePayload>(initialValues);
  const [error, setError] = useState("");
  const [selectedColaborator, setSelectedColaborator] =
    useState<Collaborator | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [companyBlock, setCompanyBlock] = useState<BlockStatus | null>(null);
  const [softBlock, setSoftBlock] = useState<BlockStatus | null>(null);
  const [notePromptCollaboratorId, setNotePromptCollaboratorId] =
    useState<Id>(undefined);
  const [selectedProntuarioCollaboratorId, setSelectedProntuarioCollaboratorId] =
    useState<Id>(undefined);
  const { id } = useParams();

  const clearFormValues = useCallback(() => {
    setFormValues(initialValues);
    setSelectedColaborator(null);
    setCompanyBlock(null);
    setSoftBlock(null);
  }, []);

  const resetFormState = useCallback(() => {
    setError("");
    setSuccess(false);
    clearFormValues();
    setNotePromptCollaboratorId(undefined);
  }, [clearFormValues]);

  // Qualquer edição encerra o aviso do cadastro anterior: sem isso o
  // formulário continua "em sucesso" enquanto o usuário digita os dados
  // do próximo atendimento.
  const updateFormValues = useCallback(
    (changes: Partial<CreateServicePayload>) => {
      setFormValues((previousValues) => ({ ...previousValues, ...changes }));
      setSuccess(false);
    },
    []
  );

  const fetchService = useCallback(async (): Promise<void> => {
    try {
      const serviceRes = await getServiceById(getUserId(), id);
      const collaboratorRes = await getCollaboratorByName(
        serviceRes.colaborador,
        serviceRes.id_empresa
      );

      setFormValues({
        collaborator_id: String(serviceRes.id_colaborador),
        company_id: String(serviceRes.id_empresa),
        area: serviceRes.area,
        department: serviceRes.setor,
        city: serviceRes.cidade,
        date_of_service: removeHoursFromDate(serviceRes.atendimento),
        annotations: serviceRes.observacoes,
        complaint: serviceRes.queixa_inicial,
        service_status:
          (serviceRes.service_status as ServiceStatus) ?? "Realizado",
        edit_reason: "",
      });
      // A busca é por nome (ilike) e pode devolver homônimos — o id manda
      setSelectedColaborator(
        collaboratorRes.find(
          (collaborator) =>
            collaborator.id_colaborador === serviceRes.id_colaborador
        ) ??
          collaboratorRes[0] ??
          null
      );
    } catch (err: unknown) {
      setError(extractErrorMessage(err, "Erro ao carregar o atendimento."));
      setSuccess(false);
    }
  }, [id]);

  const fetchCompanyUpsells = useCallback(async (companyId: Id): Promise<void> => {
    if (!companyId) {
      setCompanyBlock(null);
      setSoftBlock(null);
      return;
    }

    try {
      const [blockStatusResponse, softBlockResponse] = await Promise.all([
        getBlockStatus(companyId),
        getSoftBlockStatus(companyId),
      ]);
      setCompanyBlock(blockStatusResponse);
      setSoftBlock(softBlockResponse);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // O botão já fica desabilitado durante o envio, mas o Enter no formulário
    // ainda dispara o submit e duplicaria o atendimento.
    if (loading) return;

    setLoading(true);

    try {
      if (isEdit) {
        await updateService(getUserId(), id, formValues);
      } else {
        const registeredCollaboratorId = formValues.collaborator_id;

        await createService(formValues);

        // Limpa os dados aqui, e não só no botão "começar novo cadastro":
        // o próximo atendimento não pode herdar o paciente anterior.
        clearFormValues();
        setNotePromptCollaboratorId(registeredCollaboratorId);
      }

      setError("");
      setSuccess(true);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, "Erro ao salvar o atendimento."));
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const isCollaboratorBlocked = useMemo(
    () => selectedColaborator?.bloqueio,
    [selectedColaborator]
  );
  const isCompanyBlockedForCollaborator = useMemo(
    () => Boolean(selectedColaborator?.bloqueio_empresa),
    [selectedColaborator]
  );
  const isSubmitDisabled = useMemo<boolean>(() => {
    // continua desabilitando se:
    // - o form está incompleto, ou
    // - a empresa está bloqueada (bloqueio full tratado pelo backend)
    return (
      isSuccessOrAllRequiredFieldsAreFilled(
        success,
        formValues,
        optionalFields
      ) || (companyBlock?.is_blocked ?? false)
    );
  }, [success, formValues, companyBlock]);

  const isAdmin = useMemo<boolean>(isUserAdministrator, []);
  const isEdit = useMemo<boolean>(() => Boolean(id), [id]);
  const minInputDate = useMemo<string>(getFirstDayOfMonth, []);
  const maxInputDate = useMemo<string>(getToday, []);

  useEffect(() => {
    if (!id) return;

    fetchService();
  }, [id, fetchService]);

  useEffect(() => {
    if (!formValues.company_id) {
      setCompanyBlock(null);
      setSoftBlock(null);
      return;
    }

    fetchCompanyUpsells(formValues.company_id);
  }, [formValues.company_id, fetchCompanyUpsells]);

  return (
    <PageContainer
      title="Adicionar atendimento"
      error={error}
      guard={isUserPsychologist}
    >
      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
      >
        <CompanySelect
          companyId={formValues.company_id}
          setCompanyId={(companyId: Id) => {
            // O colaborador e os campos preenchidos a partir dele pertencem à
            // empresa anterior; mantê-los enviaria um par empresa/colaborador
            // inconsistente.
            setSelectedColaborator(null);
            updateFormValues({
              company_id: companyId as string,
              collaborator_id: "",
              area: "",
              department: "",
              city: "",
            });
          }}
          setError={setError}
          disabled={isEdit}
        />

        {Boolean(formValues?.company_id) && (
          <CollaboratorSelect
            initialValue={selectedColaborator}
            collaboratorId={selectedColaborator?.id_colaborador}
            companyId={formValues.company_id}
            setCollaborator={(collaborator: Collaborator) => {
              setSelectedColaborator(collaborator ?? null);
              updateFormValues({
                collaborator_id: collaborator?.id_colaborador
                  ? String(collaborator.id_colaborador)
                  : "",
                area: collaborator?.cargo ?? "",
                department: collaborator?.setor ?? "",
                city: collaborator?.unidade ?? "",
              });
            }}
            setError={setError}
            disabled={isEdit}
          />
        )}

        {companyBlock?.is_blocked && (
          <Alert severity="error">
            <strong>
              {companyBlock?.status_message?.title ??
                "Empresa temporariamente bloqueada."}
            </strong>
            <br />
            {companyBlock?.status_message?.description ??
              "Novos atendimentos não podem ser cadastrados até a regularização."}
          </Alert>
        )}

        {softBlock?.is_blocked && (
          <Alert severity="warning">
            <strong>
              {softBlock?.status_message?.title ?? "Sessões extras bloqueadas."}
            </strong>
            <br />
            {softBlock?.status_message?.description ??
              "O colaborador possui sessões adicionais em aberto. Sessões particulares serão lançadas como particulares até a regularização."}
          </Alert>
        )}

        {isCompanyBlockedForCollaborator && (
          <Alert severity="warning">
            <strong>
              A empresa deste colaborador possui bloqueios ativos.
            </strong>
            <br />
            Entre em contato com a eezycare antes de registrar um novo
            atendimento ou aguarde a regularização.
          </Alert>
        )}

        {Boolean(formValues?.collaborator_id) && (
          <>
            <FormGroup>
              <FormLabel>Área</FormLabel>
              <TextField disabled name="area" value={formValues.area} />
              <FormHelperText>
                Campo preenchido automaticamente com base nos dados do colaborador
              </FormHelperText>
            </FormGroup>
            <FormGroup>
              <FormLabel>Setor</FormLabel>
              <TextField
                disabled
                name="department"
                value={formValues.department}
              />
              <FormHelperText>
                Campo preenchido automaticamente com base nos dados do colaborador
              </FormHelperText>
            </FormGroup>
            <FormGroup>
              <FormLabel>Unidade</FormLabel>
              <TextField disabled name="city" value={formValues.city} />
              <FormHelperText>
                Campo preenchido automaticamente com base nos dados do colaborador
              </FormHelperText>
            </FormGroup>
            <FormGroup>
              <FormLabel>Data de atendimento</FormLabel>
              <TextField
                name="date_of_service"
                value={formValues.date_of_service}
                onChange={(e) => {
                  updateFormValues({ date_of_service: e.target.value as string });
                }}
                type="date"
                inputProps={
                  isAdmin
                    ? { max: maxInputDate }
                    : { min: minInputDate, max: maxInputDate }
                }
              />
            </FormGroup>
            <FormGroup>
              <FormLabel>Status do atendimento</FormLabel>
              <Select
                value={formValues.service_status}
                onChange={(e) => {
                  updateFormValues({
                    service_status: e.target.value as ServiceStatus,
                  });
                }}
              >
                <MenuItem value="Realizado">Realizado</MenuItem>
                <MenuItem value="Paciente Faltou">Paciente Faltou</MenuItem>
              </Select>
            </FormGroup>
            <FormGroup>
              <FormLabel>Observações</FormLabel>
              <TextField
                name="annotations"
                value={formValues.annotations}
                onChange={(e) => {
                  updateFormValues({ annotations: e.target.value as string });
                }}
              />
              <OptionalFieldHelperText />
            </FormGroup>
            <FormGroup>
              <FormLabel>Queixa inicial</FormLabel>
              <TextField
                name="complaint"
                value={formValues.complaint}
                onChange={(e) => {
                  updateFormValues({ complaint: e.target.value as string });
                }}
              />
              <OptionalFieldHelperText />
            </FormGroup>

            {isEdit && (
              <FormGroup>
                <FormLabel>Motivo da edição</FormLabel>
                <TextField
                  name="edit_reason"
                  value={formValues?.edit_reason ?? ""}
                  onChange={(e) => {
                    updateFormValues({ edit_reason: e.target.value as string });
                  }}
                />
                <OptionalFieldHelperText />
              </FormGroup>
            )}
          </>
        )}

        {isCollaboratorBlocked && (
          <Box
            sx={{
              width: "100%",
            }}
          >
            <Alert
              severity="warning"
              sx={{
                maxWidth: "100%",
                margin: "auto",
              }}
            >
              <Typography>
                Este colaborador está bloqueado para sessões particulares. Enquanto
                houver sessões cobertas pela empresa disponíveis na cota mensal,
                os atendimentos poderão ser registrados normalmente. Caso a cota
                seja excedida ou a empresa não possua sessões contratadas, o
                cadastro será recusado.
              </Typography>
            </Alert>
          </Box>
        )}

        <FormSaveAndResetButtons
          isSubmitDisabled={isSubmitDisabled}
          resetFormState={resetFormState}
          success={success}
          loading={loading}
          isResetFormStateDisabled={isEdit}
        />
      </form>

      <Dialog
        open={Boolean(notePromptCollaboratorId)}
        onClose={() => setNotePromptCollaboratorId(undefined)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          Deseja fazer alguma anotação sobre a sessão?
          <Tooltip title="Fechar">
            <IconButton
              aria-label="Fechar"
              onClick={() => setNotePromptCollaboratorId(undefined)}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Você pode abrir o prontuário do paciente agora para registrar uma
            anotação clínica sobre este atendimento.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
          <Button
            color="inherit"
            variant="outlined"
            onClick={() => setNotePromptCollaboratorId(undefined)}
          >
            Agora não
          </Button>
          <Button
            variant="contained"
            startIcon={<MedicalServicesIcon />}
            onClick={() => {
              setSelectedProntuarioCollaboratorId(notePromptCollaboratorId);
              setNotePromptCollaboratorId(undefined);
            }}
          >
            Prontuário do paciente
          </Button>
        </DialogActions>
      </Dialog>

      <ProntuarioDrawer
        idColaborador={selectedProntuarioCollaboratorId}
        open={Boolean(selectedProntuarioCollaboratorId)}
        onClose={() => setSelectedProntuarioCollaboratorId(undefined)}
        presentation="dialog"
      />
    </PageContainer>
  );
};

export default RegisterService;
